/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { QuotationResponse } from '@/api/bol';
import {
  formatDateTime,
  getEntityDisplayName,
  formatLocation,
  formatExternalReferences,
  formatSpecialCharacteristics,
  formatCurrency,
  formatValueWithUnit,
  joinUniqueValues,
  renderChip,
} from '@/utils/datagridFormatters';

interface QuotationColumnsProps {
  onViewDetails?: (quotationId: string) => void;
  onDelete: (quotation: QuotationResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createQuotationColumns = ({ onViewDetails: _onViewDetails, onDelete, canDelete = true, deleteTooltip = '' }: QuotationColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { id: any; }) => {
      return row.id || '-';
    },
  },
  {
    field: 'submissionDateTime',
    headerName: 'Submission Date',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { submissionDateTime: string | null | undefined; }) => {
      return formatDateTime(row.submissionDateTime);
    },
  },
  {
    field: 'quotationStatus',
    headerName: 'Status',
    flex: 1,
    minWidth: 120,
    renderCell: (params: { row: { quotationStatus: string | null | undefined; }; }) => renderChip('quotationStatus', params.row.quotationStatus),
  },
  {
    field: 'paymentStatus',
    headerName: 'Payment Status',
    flex: 1,
    minWidth: 120,
    renderCell: (params: { row: { paymentStatus: string | null | undefined; }; }) => renderChip('paymentStatus', params.row.paymentStatus),
  },
  {
    field: 'totalAmountDue',
    headerName: 'Total Amount Due',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { financialSummary: { totalAmountDue: any; }; totalAmountDue: any; }) => {
      // Use financialSummary.totalAmountDue (recommended) or fallback to deprecated totalAmountDue
      const amount = row.financialSummary?.totalAmountDue || row.totalAmountDue;
      return formatCurrency(amount?.value, amount?.unit);
    },
  },
  {
    field: 'requestor',
    headerName: 'Requestor',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { requestor: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }) => {
      return getEntityDisplayName(row.requestor);
    },
  },
  {
    field: 'provider',
    headerName: 'Provider',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { provider: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }) => {
      return getEntityDisplayName(row.provider);
    },
  },
  {
    field: 'startLocation',
    headerName: 'Origin',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { startLocation: { location?: { address?: { addressLocality?: string | null; postalCode?: string | null; } | null; } | null; } | null | undefined; }) => {
      return formatLocation(row.startLocation);
    },
  },
  {
    field: 'endLocation',
    headerName: 'Destination',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { endLocation: { location?: { address?: { addressLocality?: string | null; postalCode?: string | null; } | null; } | null; } | null | undefined; }) => {
      return formatLocation(row.endLocation);
    },
  },
  {
    field: 'validityEndDateTime',
    headerName: 'Valid Until',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { validityEndDateTime: string | null | undefined; }) => {
      return formatDateTime(row.validityEndDateTime);
    },
  },
  {
    field: 'quotationName',
    headerName: 'Name',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { quotationName: any; }) => {
      return row.quotationName || '-';
    },
  },
  {
    field: 'quotationProcess',
    headerName: 'Process',
    flex: 1,
    minWidth: 120,
    renderCell: (params: { row: { quotationProcess: string | null | undefined; }; }) => renderChip('quotationProcess', params.row.quotationProcess, 'outlined'),
  },
  {
    field: 'batchesCount',
    headerName: 'Batches',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { batchesOfGoods: string | any[]; }) => {
      return row.batchesOfGoods?.length || 0;
    },
  },
  {
    field: 'validityStartDateTime',
    headerName: 'Valid From',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { validityStartDateTime: string | null | undefined; }) => {
      return formatDateTime(row.validityStartDateTime);
    },
  },
  {
    field: 'incoterm',
    headerName: 'Incoterm',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { incoterm: any; }) => {
      return row.incoterm || '-';
    },
  },
  {
    field: 'declaredValue',
    headerName: 'Declared Value',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { declaredValue: { value: number | null | undefined; unit: string | null | undefined; }; }) => {
      return formatValueWithUnit(row.declaredValue?.value, row.declaredValue?.unit);
    },
  },
  {
    field: 'financialTotalExclTax',
    headerName: 'Total (excl. Tax)',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { financialSummary: { totalAmountTaxExcluded: any; }; }) => {
      const amount = row.financialSummary?.totalAmountTaxExcluded;
      return formatCurrency(amount?.value, amount?.unit);
    },
  },
  {
    field: 'financialTotalInclTax',
    headerName: 'Total (incl. Tax)',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { financialSummary: { totalAmountTaxIncluded: any; }; }) => {
      const amount = row.financialSummary?.totalAmountTaxIncluded;
      return formatCurrency(amount?.value, amount?.unit);
    },
  },
  {
    field: 'financialAmountDue',
    headerName: 'Amount Due',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { financialSummary: { totalAmountDue: any; }; }) => {
      const amount = row.financialSummary?.totalAmountDue;
      return formatCurrency(amount?.value, amount?.unit);
    },
  },
  {
    field: 'requestedPrepaidAmount',
    headerName: 'Prepaid Requested',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { requestedPrepaidAmount: { value: number | null | undefined; unit: string | null | undefined; }; }) => {
      return formatCurrency(row.requestedPrepaidAmount?.value, row.requestedPrepaidAmount?.unit);
    },
  },
  {
    field: 'prepaidAmount',
    headerName: 'Prepaid Actual',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { prepaidAmount: { value: number | null | undefined; unit: string | null | undefined; }; }) => {
      return formatCurrency(row.prepaidAmount?.value, row.prepaidAmount?.unit);
    },
  },
  {
    field: 'serviceOfferName',
    headerName: 'Service Offer Name',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { serviceOffer: { commercialProductName: any; }; }) => {
      return row.serviceOffer?.commercialProductName || '-';
    },
  },
  {
    field: 'serviceOfferProvider',
    headerName: 'Service Offer Provider',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { serviceOffer: { provider: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }; }) => {
      return getEntityDisplayName(row.serviceOffer?.provider);
    },
  },
  {
    field: 'serviceOfferValidUntil',
    headerName: 'Service Offer Valid Until',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { serviceOffer: { validityEndDateTime: string | null | undefined; }; }) => {
      return formatDateTime(row.serviceOffer?.validityEndDateTime);
    },
  },
  {
    field: 'saleOperationsCount',
    headerName: 'Sale Operations',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { saleOperations: string | any[]; }) => {
      return row.saleOperations?.length || 0;
    },
  },
  {
    field: 'saleOperationTypes',
    headerName: 'Operation Types',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { saleOperations: { operationType?: string; }[]; }) => {
      const types = row.saleOperations?.map((op: { operationType?: string }) => op.operationType).filter(Boolean);
      return joinUniqueValues(types);
    },
  },
  {
    field: 'saleOperationCodes',
    headerName: 'Operation Codes',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { saleOperations: { operationCode?: string; }[]; }) => {
      const codes = row.saleOperations?.map((op: { operationCode?: string }) => op.operationCode).filter(Boolean);
      return joinUniqueValues(codes);
    },
  },
  {
    field: 'issuer',
    headerName: 'Issuer',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { issuer: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }) => {
      return getEntityDisplayName(row.issuer);
    },
  },
  {
    field: 'invoicedParty',
    headerName: 'Invoiced Party',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { invoicedParty: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }) => {
      return getEntityDisplayName(row.invoicedParty);
    },
  },
  {
    field: 'contactRoles',
    headerName: 'Contact Roles',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { contacts: { role?: string; }[]; }) => {
      const roles = row.contacts?.map((contact: { role?: string }) => contact.role).filter(Boolean);
      return joinUniqueValues(roles);
    },
  },
  {
    field: 'documentsCount',
    headerName: 'Documents',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { documents: string | any[]; }) => {
      return row.documents?.length || 0;
    },
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { externalReferences: { externalReferenceType?: string | null; reference?: string | null; }[] | null | undefined; }) => {
      return formatExternalReferences(row.externalReferences);
    },
  },
  {
    field: 'specialInstructionsCount',
    headerName: 'Special Instructions',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { specialInstructions: string | any[]; }) => {
      return row.specialInstructions?.length || 0;
    },
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { specialCharacteristics: { specialCharacteristicType?: string | null; characteristicReference?: string | null; stringValue?: string | null; numericalValue?: { value?: number; unit?: string; } | null; }[] | null | undefined; }) => {
      return formatSpecialCharacteristics(row.specialCharacteristics);
    },
  },
  {
    field: 'quotationRequestLink',
    headerName: 'Linked Quotation Request',
    flex: 1.5,
    minWidth: 200,
    renderCell: (params: { row: { quotationRequest: any; }; }) => {
      if (!params.row.quotationRequest) return '-';
      
      let uri = '';
      // Handle both string and Link object formats
      if (typeof params.row.quotationRequest === 'string') {
        uri = params.row.quotationRequest;
      } else if (typeof params.row.quotationRequest === 'object' && params.row.quotationRequest.uri) {
        // BOL Link object has uri property
        uri = params.row.quotationRequest.uri;
      }
      
      if (!uri) return '-';
      
      return (
        <a
          href={uri.startsWith('http') ? uri : `https://${uri}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#1976d2',
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            display: 'block'
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLElement).style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.textDecoration = 'none';
          }}
        >
          {uri}
        </a>
      );
    },
  },
  {
    field: 'carbonFootprint',
    headerName: 'Carbon Footprint',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { carbonFootprint: { value: number | null | undefined; unit: string | null | undefined; }; }) => {
      return formatValueWithUnit(row.carbonFootprint?.value, row.carbonFootprint?.unit);
    },
  },
  {
    field: 'rejectionCodes',
    headerName: 'Rejection Codes',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { rejections: { code?: string; }[]; }) => {
      const codes = row.rejections?.map((rejection: { code?: string }) => rejection.code).filter(Boolean);
      return joinUniqueValues(codes);
    },
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    flex: 1,
    minWidth: 120,
    hideable: false, // Always visible in column selector
    getActions: (params: { row: QuotationResponse; }) => [
      <IconButton
        key="view"
        component={Link}
        to={`/quotations/${params.row.id}`}
        size="small"
        color="primary"
        aria-label="View Details"
        sx={{ padding: '8px' }}
      >
        <Visibility fontSize="small" />
      </IconButton>,
      <Tooltip title={!canDelete ? deleteTooltip : ''} key="delete-tooltip">
        <span>
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => onDelete(params.row)}
            disabled={!canDelete}
            color="error"
          />
        </span>
      </Tooltip>,
    ],
  },
];

export const defaultQuotationColumnVisibilityModel = {
  // Default visible columns
  id: true,
  submissionDateTime: true,
  quotationStatus: true,
  paymentStatus: true,
  totalAmountDue: true,
  requestor: true,
  provider: true,
  startLocation: true,
  endLocation: true,
  validityEndDateTime: true,
  actions: true,
  // Hidden by default
  quotationName: false,
  quotationProcess: false,
  batchesCount: false,
  validityStartDateTime: false,
  incoterm: false,
  declaredValue: false,
  financialTotalExclTax: false,
  financialTotalInclTax: false,
  financialAmountDue: false,
  requestedPrepaidAmount: false,
  prepaidAmount: false,
  serviceOfferName: false,
  serviceOfferProvider: false,
  serviceOfferValidUntil: false,
  saleOperationsCount: false,
  saleOperationTypes: false,
  saleOperationCodes: false,
  issuer: false,
  invoicedParty: false,
  contactRoles: false,
  documentsCount: false,
  externalReferences: false,
  specialInstructionsCount: false,
  specialCharacteristics: false,
  quotationRequestLink: false,
  carbonFootprint: false,
  rejectionCodes: false,
};